/** Spec 010 directory contracts. No attendance calculation fields belong here. */
export type DirectoryId = string;
export interface DirectoryActor {
  userId: number;
}
export interface UtcRange {
  fromMs: number | null;
  toMs: number | null;
}
export interface DateRange {
  fromDate: string;
  toDateExclusive: string | null;
}
export type DirectoryScope =
  | { kind: 'company' }
  | { kind: 'brigade'; brigadeId: string }
  | { kind: 'site'; siteId: string };
export const DIRECTORY_CAPABILITIES = [
  'personnel.read',
  'personnel.select',
  'personnel.create',
  'personnel.update',
  'source_evidence.read',
  'identity.review',
  'identity.correct',
  'accounts.person_select',
  'accounts.person_bind',
  'brigade.read',
  'brigade.rename',
  'brigade.create_own',
  'brigade.manage',
  'membership.read',
  'membership.manage',
  'membership.correct_history',
  'assignments.manage',
  'cases.manage',
  'source_mapping.manage',
  'jobs.retry',
  'directory.import',
  'directory.export',
] as const;
export type DirectoryCapability = (typeof DIRECTORY_CAPABILITIES)[number];
export type DirectoryRole = 'company_operator' | 'brigade_primary' | 'brigade_representative' | 'site_master';
export interface ExpectedRevision {
  kind: string;
  id: string;
  revision: number;
}
export interface DirectoryCommand<T> {
  request_id: string;
  expectedRevisions: ExpectedRevision[];
  previewToken?: string;
  input: T;
}
export interface DirectoryResult<T> {
  data: T;
  revisions: ExpectedRevision[];
  request_id?: string;
  processing: 'ready' | 'pending';
}
export interface DirectoryPage<T> {
  items: T[];
  nextCursor: string | null;
  snapshotRevision: string;
}
export interface DirectoryFreshness {
  state: 'fresh' | 'pending' | 'failed';
  sourceRevision: string;
  processedRevision: string;
  computedAtMs: number | null;
  asOfMs: number;
  validThroughMs: number | null;
}
export type IdentityAction =
  | 'same_person'
  | 'different_people'
  | 'new_person'
  | 'defer'
  | 'correct'
  | 'split'
  | 'merge_persons';
export interface IdentityInput {
  action: IdentityAction;
  targetPersonId?: string;
  survivingPersonId?: string;
  personIds: string[];
  registrationIds: string[];
  range?: UtcRange;
  relationCoverage?: [string, string][];
  basis: string;
  restoreDecisionId?: string;
  resolveDisputeIds?: string[];
  membershipResolution?: { moveToSurvivor: true };
}
export interface PersonSummary {
  id: string;
  canonicalId: string;
  displayName: string | null;
  state: 'provisional' | 'confirmed' | 'joined';
  profileRevision: number;
  identityRevision: number;
}
/** Bounded, complete navigation index. Equal nameKey values are search hints, not identity decisions. */
export interface DirectoryBrowseItem extends PersonSummary {
  nameKey: string;
  sites: { id: string; name: string }[];
  /** Actual, uniquely resolved past passes in the requested half-open range; not registrations. */
  passSites: { id: string; name: string; eventCount: number; lastEventMs: number }[];
  registrationCount: number;
  lastEventMs: number | null;
  recentSiteCount: number;
  employmentStatus: 'active' | 'dismissed' | 'unknown' | 'status_conflict';
  brigades: { id: string; name: string }[];
}
export interface DirectoryBrowseIndex {
  items: DirectoryBrowseItem[];
  snapshotRevision: string;
  asOfMs: number;
  windowMs: number;
  passRange: UtcRange;
}
export interface DirectoryBrowseOptions {
  passFromMs?: number;
  passToMs?: number;
}
export interface RegistrationSummary {
  id: string;
  siteId: string;
  siteName?: string;
  sourceInstanceId: string;
  localPersonId: string;
  initialPersonId: string;
  observationRevision: number;
  aliasRevision: number;
  names: string[];
  localStatus: 'active' | 'dismissed' | 'unknown' | 'status_conflict';
  lastEventMs: number | null;
}
export interface PersonCard extends PersonSummary {
  basis: string | null;
  registrations: RegistrationSummary[];
  allowedActions: DirectoryCapability[];
  memberships?: {
    id: string;
    brigadeId: string;
    brigadeName: string;
    fromDate: string;
    toDateExclusive: string | null;
    revision: number;
  }[];
}
export interface IdentityResolution {
  state: 'confirmed' | 'provisional' | 'unresolved';
  personId?: string;
  aliasVersion?: string;
  reasonCode?: string;
  identityRevision: number;
}
export interface IdentityPreview {
  previewToken: string;
  expiresAtMs: number;
  expectedRevisions: ExpectedRevision[];
  input: IdentityInput;
  effects: { personIds: string[]; registrationIds: string[]; action: IdentityAction };
  conflicts: { code: string }[];
}
export interface CandidateEdge {
  leftId: string;
  rightId: string;
  score: number;
  tier: string;
  reasons: string[];
  conflicting: boolean;
}
export interface CandidateGroup {
  id: string;
  registrationIds: string[];
  registrations: RegistrationSummary[];
  edges: CandidateEdge[];
  state: 'compatible' | 'conflict' | 'deferred' | 'reviewed' | 'automatic';
  reviewReason?: string;
  displayName: string | null;
}
export interface CandidateQueue extends DirectoryPage<CandidateGroup> {
  asOfMs: number;
  validThroughMs: number;
  windowMs: number;
  matcherVersion: string;
  policyVersion: string;
  freshness: DirectoryFreshness;
  readiness?: { state: 'ready' | 'pending' | 'failed'; reasonCode?: string; refreshAfterMs?: number };
}
export interface AcceptedDirectoryFact {
  sourceKey: string;
  siteId: string;
  sourceInstanceId: string | null;
  localPersonId: string;
  eventMs: number;
  receivedAtMs?: number;
  name?: string | null;
  passId?: number | null;
  sourceVersion?: string;
}
export interface DirectoryObservationInput {
  sourceKey: string;
  siteId: string;
  sourceInstanceId: string | null;
  localPersonId: string;
  receivedAtMs?: number;
  exportedAtMs?: number | null;
  sourceVersion?: string;
  orderingBasis?: string;
  fields: Partial<
    Record<'name' | 'section' | 'department' | 'brigade' | 'position' | 'status', string | null>
  >;
}
export interface DirectoryFilters {
  query?: string;
  state?: string;
  siteId?: string;
  brigadeId?: string;
  cursor?: string;
  limit?: number;
}
